/**
 * Clip Manipulation Utilities
 * 
 * Helper functions for traversing and modifying recursive clip structures
 * (Project -> Clips -> SyntheData -> Clips...)
 */

import { Clip } from '../types';

type ClipSourceLike = Pick<Clip, 'url' | 'localUrl' | 'streamUrl'>;

export const isStreamingManifestUrl = (url?: string | null): boolean => {
    if (!url) return false;
    const normalized = url.toLowerCase();
    return normalized.includes('.m3u8') ||
        normalized.includes('/manifest/video.m3u8') ||
        normalized.includes('/dash/') ||
        normalized.includes('.mpd');
};

export const getClipLocalSourceUrl = (clip: Pick<Clip, 'url' | 'localUrl'>): string | undefined => {
    if (clip.localUrl) return clip.localUrl;
    if (clip.url?.startsWith('blob:')) return clip.url;
    return undefined;
};

interface ClipSourceOptions {
    preferLocal?: boolean;
    includeStreamUrl?: boolean;
    allowStreamingManifest?: boolean;
}

export const getClipSourceUrl = (
    clip: ClipSourceLike,
    {
        preferLocal = true,
        includeStreamUrl = true,
        allowStreamingManifest = false,
    }: ClipSourceOptions = {}
): string | undefined => {
    const localUrl = getClipLocalSourceUrl(clip);
    const candidates = preferLocal
        ? [localUrl, clip.url, includeStreamUrl ? clip.streamUrl : undefined]
        : [clip.url, localUrl, includeStreamUrl ? clip.streamUrl : undefined];

    for (const candidate of candidates) {
        if (!candidate) continue;
        if (!allowStreamingManifest && isStreamingManifestUrl(candidate)) continue;
        return candidate;
    }

    return undefined;
};

export const getClipPreviewUrl = (clip: ClipSourceLike): string | undefined => {
    const localUrl = getClipLocalSourceUrl(clip);
    if (localUrl) return localUrl;
    if (clip.streamUrl && !isStreamingManifestUrl(clip.streamUrl)) return clip.streamUrl;
    if (clip.url && !isStreamingManifestUrl(clip.url)) return clip.url;
    return undefined;
};

export const getClipExportSourceUrl = (clip: ClipSourceLike): string | undefined => {
    return getClipSourceUrl(clip, {
        preferLocal: true,
        includeStreamUrl: true,
        allowStreamingManifest: false,
    });
};

/**
 * Update a specific clip by ID deep within the clip tree.
 * Replaces the clip with the result of the updater function.
 * Returns a new array with the update applied (immutable).
 */
export const updateClipDeep = (
    clips: Clip[],
    targetId: string,
    updater: (clip: Clip) => Clip
): Clip[] => {
    return clips.map(c => {
        // Match found
        if (c.id === targetId) {
            return updater(c);
        }

        // Recursive search in syntheData
        if (c.syntheData?.clips) {
            return {
                ...c,
                syntheData: {
                    ...c.syntheData,
                    clips: updateClipDeep(c.syntheData.clips, targetId, updater)
                }
            };
        }

        return c;
    });
};

/**
 * Find a clip by ID deep within the clip tree.
 * Returns the clip or undefined if not found.
 */
export const findClipDeep = (clips: Clip[], targetId: string): Clip | undefined => {
    for (const clip of clips) {
        if (clip.id === targetId) return clip;

        if (clip.syntheData?.clips) {
            const found = findClipDeep(clip.syntheData.clips, targetId);
            if (found) return found;
        }
    }
    return undefined;
};

export const flattenClipsDeep = (clips: Clip[]): Clip[] => {
    const flattened: Clip[] = [];

    for (const clip of clips) {
        flattened.push(clip);
        if (clip.syntheData?.clips) {
            flattened.push(...flattenClipsDeep(clip.syntheData.clips));
        }
    }

    return flattened;
};

export const getRemoteVideoClipsNeedingLocalCache = (clips: Clip[]): Clip[] => {
    return flattenClipsDeep(clips).filter(clip => {
        if (clip.type !== 'video') return false;
        if (getClipLocalSourceUrl(clip)) return false;

        return !!getClipSourceUrl(clip, {
            preferLocal: false,
            includeStreamUrl: true,
            allowStreamingManifest: false,
        });
    });
};

/**
 * Delete a clip by ID deep within the clip tree.
 * Returns a new array with the clip removed.
 */
export const deleteClipDeep = (clips: Clip[], targetId: string): Clip[] => {
    return clips
        .filter(c => c.id !== targetId)
        .map(c => {
            if (c.syntheData?.clips) {
                return {
                    ...c,
                    syntheData: {
                        ...c.syntheData,
                        clips: deleteClipDeep(c.syntheData.clips, targetId)
                    }
                };
            }
            return c;
        });
};

/**
 * Apply a transformation to all clips in the tree
 */
export const mapClipsDeep = (clips: Clip[], mapper: (clip: Clip) => Clip): Clip[] => {
    return clips.map(c => {
        const mapped = mapper(c);

        if (mapped.syntheData?.clips) {
            return {
                ...mapped,
                syntheData: {
                    ...mapped.syntheData,
                    clips: mapClipsDeep(mapped.syntheData.clips, mapper)
                }
            };
        }

        return mapped;
    });
};
